import { easeInOutCubic } from './tourGeometry';

/**
 * DOM choreography for the guided tour. Kept apart from tourGeometry so the
 * math stays pure and unit-testable.
 */

export interface Cancellable<T> {
  finished: Promise<T>;
  cancel: () => void;
}

/** A cancellable pause — the tour's dwell beats between moves. */
export function sleep(ms: number): Cancellable<void> {
  let done: () => void;
  const finished = new Promise<void>((res) => (done = res));
  const id = window.setTimeout(() => done(), ms);
  return { finished, cancel: () => window.clearTimeout(id) };
}

/**
 * Two animation frames: enough for a state change made just before to render
 * and lay out, so what's measured next is the new page, not the old one.
 */
export function nextLayout(): Cancellable<void> {
  let raf = 0;
  let done: () => void;
  const finished = new Promise<void>((res) => (done = res));
  raf = requestAnimationFrame(() => {
    raf = requestAnimationFrame(() => done());
  });
  return { finished, cancel: () => cancelAnimationFrame(raf) };
}

/** Frames a target must hold still before it's measured. */
const SETTLED_FRAMES = 3;

/**
 * Wait for the step's [data-tour] target to exist, have layout AND hold still
 * (its place on the page unchanged for a few frames). Holding still matters
 * when the step just changed what's above it — a shot chip adds or drops a
 * card — and the report takes a while to re-render: measured mid-change, the
 * scroll lands where the target WAS. Resolves null on timeout when the target
 * never mounted — the caller skips the step instead of hanging the tour — and
 * the target as it is when it mounted but never stopped moving.
 *
 * The timeout is the SKIP latency, not a safety margin: a step whose target
 * will never mount (single-block reports, a basic report with no tips) waits
 * this long showing a flat dim with no cutout, no card and nothing moving,
 * which reads as a hang. Targets are rendered synchronously by the tab switch
 * that precedes them and only need a few frames to get layout, so this is
 * sized just above that rather than at the old two seconds.
 */
export function resolveTarget(
  root: HTMLElement,
  target: string,
  timeoutMs = 600,
): Cancellable<HTMLElement | null> {
  let raf = 0;
  let done: (el: HTMLElement | null) => void;
  const finished = new Promise<HTMLElement | null>((res) => (done = res));
  const deadline = performance.now() + timeoutMs;
  let lastTop: number | null = null;
  let still = 0;
  const tick = () => {
    const el = root.querySelector<HTMLElement>(`[data-tour="${target}"]`);
    const rect = el?.getBoundingClientRect();
    if (el && rect && rect.width > 0) {
      const top = rect.top + window.scrollY;
      still = top === lastTop ? still + 1 : 0;
      lastTop = top;
      if (still >= SETTLED_FRAMES || performance.now() >= deadline) {
        done(el);
        return;
      }
    } else if (performance.now() >= deadline) {
      done(null);
      return;
    }
    raf = requestAnimationFrame(tick);
  };
  tick();
  return { finished, cancel: () => cancelAnimationFrame(raf) };
}

/**
 * Eased window scroll driven by rAF. Old Safari webviews ignore
 * `behavior: 'smooth'`, so the tour brings its own animation; `instant`
 * (reduced motion) jumps in one frame.
 *
 * The duration is deliberately unhurried: the spotlight cutout is chasing the
 * target the whole way, and on the older phones this webview ships to, a brisk
 * travel turned into a stutter rather than a glide.
 */
export function animateWindowScroll(
  to: number,
  { durationMs = 880, instant = false } = {},
): Cancellable<void> {
  const from = window.scrollY;
  const delta = to - from;
  if (instant || Math.abs(delta) < 2) {
    window.scrollTo(0, to);
    return { finished: Promise.resolve(), cancel: () => {} };
  }
  let raf = 0;
  let done: () => void;
  const finished = new Promise<void>((res) => (done = res));
  const start = performance.now();
  const tick = (now: number) => {
    const t = Math.min(1, (now - start) / durationMs);
    window.scrollTo(0, from + delta * easeInOutCubic(t));
    if (t >= 1) {
      done();
      return;
    }
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return { finished, cancel: () => cancelAnimationFrame(raf) };
}
